import { useEffect, useRef, useState } from "react";
import Modal from "../../../shared/components/modal/Modal";
import { useUser } from "../../../app/providers/userContext";
import { useAccountSwitcher } from "../../../shared/hooks/useAccountSwitcher";
import { getLinkedProfiles, switchLinkedProfile } from "../api/linkedProfilesApi";

export default function LinkedProfileSwitcher({ onClose }) {
  const { user } = useUser();
  const { activateProfile } = useAccountSwitcher();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);
  const [error, setError] = useState("");
  const pending = useRef(false);

  useEffect(() => {
    let active = true;
    getLinkedProfiles().then(result => {
      if (active) setAccounts(result);
    }).catch(err => {
      if (active) setError([404, 501].includes(err.response?.status)
        ? "Linked student switching is not available on this server yet. You can still switch between saved profiles."
        : err.response?.data?.message || err.message || "Unable to load linked students.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const select = async account => {
    if (pending.current) return;
    pending.current = true;
    setSwitching(account.accountId);
    setError("");
    try {
      const result = await switchLinkedProfile(account);
      await activateProfile(result.data, result.token);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not switch profiles. Please try again.");
    } finally {
      pending.current = false;
      setSwitching(null);
    }
  };

  return (
    <Modal show title="Switch student profile" onClose={switching ? undefined : onClose} isLock={Boolean(switching)} showCloseButton={false} showCrossButton={!switching} width="440px">
      <p className="text-sm text-slate-600">Students authorized for your verified mobile account. No new login is needed while your session is valid.</p>
      {loading && <p role="status" className="text-sm text-slate-600">Loading linked students...</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {!loading && !error && !accounts.length && <p className="text-sm text-slate-600">No linked student profiles are available for this session. Contact your school admin to check the mobile number and guardian access.</p>}
      <div className="space-y-2">
        {accounts.map(account => {
          const current = account.accountId === user?.accountId && account.schoolId === user?.schoolId;
          const initial = (account.name || "?").trim().charAt(0).toUpperCase();
          return <button key={account.accountId} type="button" onClick={() => select(account)} disabled={Boolean(switching) || current}
            className={`flex min-h-14 w-full items-center gap-3 rounded-xl border p-3 text-left disabled:opacity-60 ${current ? "border-indigo-200 bg-indigo-50" : "border-slate-200 bg-white active:scale-[0.99]"}`}>
            <span aria-hidden="true" className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${current ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`}>{initial}</span>
            <span className="min-w-0 flex-1 break-words"><span className="block text-sm font-semibold text-slate-900">{account.name}</span><span className="block text-xs text-slate-600">{account.schoolName}</span></span>
            <span className={`shrink-0 text-xs font-semibold ${current ? "text-indigo-700" : "text-blue-600"}`}>{current ? "Current" : switching === account.accountId ? "Switching..." : "Switch"}</span>
          </button>;
        })}
      </div>
      <button type="button" onClick={onClose} disabled={Boolean(switching)} className="mt-4 min-h-11 w-full rounded-lg border border-slate-300 text-sm font-medium text-slate-700 disabled:opacity-50">Close</button>
    </Modal>
  );
}
